import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, FileText, Calendar, Euro, User, Mail, Building2,
  CheckCircle, Clock, AlertCircle, Download, Send, Edit, X, Trash2, MessageSquare, Upload, Copy
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import LoadingSpinner from '../LoadingSpinner';
import { useToast } from '../../hooks/use-toast';
import { computeFiscalSummary, computeAccisaPerBottle, computeContrassegnoPerBottle, formatCurrencyMillesimi } from '../../utils/fiscalCalc';

const QuoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [converting, setConverting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [discountTiers, setDiscountTiers] = useState([]);

  useEffect(() => {
    if (adminToken && id) {
      loadQuote();
      loadDiscountTiers();
    }
  }, [id, adminToken]);

  const loadQuote = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotes/admin/quotes/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('Quote data received:', data);
        setQuote(data);
      } else {
        console.error('Errore nel caricamento preventivo, status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
      }
    } catch (error) {
      console.error('Errore:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDiscountTiers = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/discount-tiers`
      );
      
      if (response.ok) {
        const data = await response.json();
        setDiscountTiers(data.tiers || []);
      }
    } catch (error) {
      console.error('Errore caricamento sconti:', error);
    }
  };

  const calculatePriceWithDiscount = (basePrice, quantity) => {
    // Trova la fascia di sconto applicabile
    const applicableTier = discountTiers
      .filter(tier => tier.is_active && quantity >= tier.min_quantity && quantity <= tier.max_quantity)
      .sort((a, b) => b.discount_percentage - a.discount_percentage)[0];
    
    if (applicableTier) {
      const discount = applicableTier.discount_percentage;
      return basePrice * (1 - discount);
    }
    
    return basePrice;
  };

  const getVolumeDiscountSimulation = (item) => {
    // Calcola il prezzo base per singola unità (senza sconto)
    const basePricePerUnit = item.unit_price;
    const currentQuantity = item.quantity;
    
    // Genera simulazioni per le varie fasce
    const simulations = discountTiers
      .filter(tier => tier.is_active)
      .map(tier => {
        const avgQuantity = Math.floor((tier.min_quantity + tier.max_quantity) / 2);
        const discountedPrice = basePricePerUnit * (1 - tier.discount_percentage);
        const totalPrice = discountedPrice * avgQuantity;
        
        // Verifica se la quantità corrente è in questa fascia
        const isCurrentTier = currentQuantity >= tier.min_quantity && currentQuantity <= tier.max_quantity;
        
        return {
          label: tier.label,
          minQty: tier.min_quantity,
          maxQty: tier.max_quantity,
          avgQty: avgQuantity,
          discount: tier.discount_percentage * 100,
          pricePerUnit: discountedPrice,
          totalPrice: totalPrice,
          isCurrentTier: isCurrentTier
        };
      });
    
    return simulations;
  };

  const getNextTierSavings = (item) => {
    const currentQuantity = item.quantity;
    const basePricePerUnit = item.unit_price;
    const currentTotal = item.total;
    
    // Trova la fascia successiva con sconto maggiore
    const nextTier = discountTiers
      .filter(tier => tier.is_active && tier.min_quantity > currentQuantity)
      .sort((a, b) => a.min_quantity - b.min_quantity)[0];
    
    if (nextTier) {
      const additionalBottles = nextTier.min_quantity - currentQuantity;
      const discountedPrice = basePricePerUnit * (1 - nextTier.discount_percentage);
      const newTotal = discountedPrice * nextTier.min_quantity;
      const savings = (basePricePerUnit * nextTier.min_quantity) - newTotal;
      const savingsPercent = nextTier.discount_percentage * 100;
      
      return {
        additionalBottles,
        newQuantity: nextTier.min_quantity,
        savings,
        savingsPercent,
        newPricePerUnit: discountedPrice,
        newTotal
      };
    }
    
    return null;
  };


  const handleCloneItem = async (index) => {
    if (!quote || !quote.items || !quote.items[index]) return;

    const original = quote.items[index];
    // Create a duplicate without the id (server/model will assign a new uuid)
    const { id: _omit, ...rest } = original;
    const cloned = { ...rest };

    const newItems = [...quote.items];
    newItems.splice(index + 1, 0, cloned);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotes/admin/quotes/${id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${adminToken}`
          },
          body: JSON.stringify({ items: newItems })
        }
      );

      if (response.ok) {
        toast({
          title: "Prodotto clonato",
          description: "Una copia del prodotto è stata aggiunta al preventivo.",
        });
        loadQuote();
      } else {
        const errText = await response.text();
        throw new Error(errText || 'Errore clonazione');
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: "Impossibile clonare il prodotto.",
        variant: "destructive"
      });
    }
  };

  const handleSendEmail = async () => {
    if (!window.confirm('Vuoi inviare questo preventivo via email al cliente?')) {
      return;
    }

    try {
      setSending(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotes/admin/quotes/${id}/send-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        toast({
          title: "Email inviata!",
          description: "Il preventivo è stato inviato al cliente.",
        });
        // Ricarica preventivo per aggiornare Timeline
        loadQuote();
      } else {
        throw new Error('Errore invio email');
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: "Impossibile inviare l'email.",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotes/admin/quotes/${id}/export-pdf`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Preventivo_${quote.quote_number}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: "Impossibile scaricare il PDF.",
        variant: "destructive"
      });
    }
  };

  const handleEdit = () => {
    // Naviga alla pagina di modifica preventivo
    navigate(`/admin/quotes?edit=${id}`);
  };

  const handleDelete = async () => {
    if (!window.confirm('Sei sicuro di voler cancellare questo preventivo? Questa azione è irreversibile.')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotes/admin/quotes/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        toast({
          title: "Preventivo cancellato",
          description: "Il preventivo è stato eliminato con successo.",
        });
        navigate('/admin/quotes');
      } else {
        throw new Error('Errore cancellazione');
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: "Impossibile cancellare il preventivo.",
        variant: "destructive"
      });
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotes/admin/quotes/${id}/attachments`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          },
          body: formData
        }
      );

      if (response.ok) {
        toast({
          title: "Allegato caricato!",
          description: "Il file è stato caricato con successo.",
        });
        // Ricarica preventivo per mostrare nuovo allegato
        loadQuote();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore caricamento');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: "Errore",
        description: error.message || "Impossibile caricare il file.",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
      // Reset input
      event.target.value = '';
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'In attesa', icon: Clock },
      sent: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Inviato', icon: Send },
      approved: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Approvato', icon: CheckCircle },
      accepted: { bg: 'bg-green-100', text: 'text-green-800', label: 'Accettato', icon: CheckCircle },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rifiutato', icon: X },
      converted: { bg: 'bg-green-100', text: 'text-green-800', label: 'Convertito in ordine', icon: CheckCircle },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Annullato', icon: X }
    };
    
    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status, icon: FileText };
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-4 h-4 mr-1" />
        {config.label}
      </span>
    );
  };

  const handleApprove = async () => {
    if (!window.confirm('Vuoi approvare questo preventivo?')) {
      return;
    }

    try {
      setConverting(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotes/admin/quotes/${id}/approve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        toast({
          title: "Preventivo approvato!",
          description: "Il preventivo è stato approvato con successo.",
        });
        await loadQuote(); // Reload quote to update status
      } else {
        throw new Error('Errore approvazione');
      }
    } catch (error) {
      console.error('Errore:', error);
      toast({
        title: "Errore",
        description: "Impossibile approvare il preventivo.",
        variant: "destructive"
      });
    } finally {
      setConverting(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm('Vuoi rifiutare questo preventivo?')) {
      return;
    }

    try {
      setConverting(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotes/admin/quotes/${id}/reject`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        toast({
          title: "Preventivo rifiutato",
          description: "Il preventivo è stato rifiutato.",
        });
        await loadQuote();
      } else {
        throw new Error('Errore rifiuto');
      }
    } catch (error) {
      console.error('Errore:', error);
      toast({
        title: "Errore",
        description: "Impossibile rifiutare il preventivo.",
        variant: "destructive"
      });
    } finally {
      setConverting(false);
    }
  };

  const handleConvertToOrder = async () => {
    if (!window.confirm('Vuoi convertire questo preventivo in un ordine? Questa azione creerà un nuovo ordine per il cliente.')) {
      return;
    }

    try {
      setConverting(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotes/admin/quotes/${id}/convert-to-order`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast({
          title: "Preventivo convertito!",
          description: `Ordine ${data.orderNumber} creato con successo.`,
        });
        // Redirect to order detail
        navigate(`/admin/orders/${data.orderId}`);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Errore conversione');
      }
    } catch (error) {
      console.error('Errore:', error);
      toast({
        title: "Errore",
        description: error.message || "Impossibile convertire il preventivo.",
        variant: "destructive"
      });
    } finally {
      setConverting(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  if (!quote) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Preventivo non trovato</p>
          <Link to="/admin/quotes" className="text-purple-600 hover:text-purple-800 mt-4 inline-block">
            Torna ai preventivi
          </Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Preventivo {quote.quote_number}
                </h1>
                <p className="text-gray-500 text-sm">Emesso il {formatDate(quote.issue_date)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getStatusBadge(quote.status)}
            </div>
          </div>

          {/* Azioni */}
          <div className="flex flex-wrap gap-3">
            {/* Approve/Reject buttons - only show if pending or sent */}
            {(quote.status === 'pending' || quote.status === 'sent') && (
              <>
                <button
                  onClick={handleApprove}
                  disabled={converting}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {converting ? 'Elaborazione...' : 'Approva'}
                </button>
                <button
                  onClick={handleReject}
                  disabled={converting}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  <X className="w-4 h-4 mr-2" />
                  Rifiuta
                </button>
              </>
            )}
            
            {/* Convert to Order button - only show if approved or accepted */}
            {(quote.status === 'approved' || quote.status === 'accepted') && (
              <button
                onClick={handleConvertToOrder}
                disabled={converting}
                className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {converting ? 'Conversione...' : 'Converti in Ordine'}
              </button>
            )}
            
            <button
              onClick={handleEdit}
              className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Edit className="w-4 h-4 mr-2" />
              Modifica
            </button>
            <button
              onClick={handleDownloadPDF}
              className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              Scarica PDF
            </button>
            <button
              onClick={handleSendEmail}
              disabled={sending}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Send className="w-4 h-4 mr-2" />
              {sending ? 'Invio...' : 'Invia Email'}
            </button>
            
            {/* Upload Allegato */}
            <label className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors cursor-pointer disabled:opacity-50">
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Caricamento...' : 'Carica Allegato'}
              <input
                type="file"
                onChange={handleFileUpload}
                disabled={uploading}
                className="hidden"
                accept="image/*,.pdf,.doc,.docx"
              />
            </label>
            
            <button
              onClick={handleDelete}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Cancella
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colonna sinistra */}
          <div className="lg:col-span-2 space-y-6">
            {/* Prodotti */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                Prodotti
              </h2>
              <div className="space-y-6">
                {quote.items?.map((item, index) => {
                  const simulations = getVolumeDiscountSimulation(item);
                  
                  return (
                    <div key={index} className="border-b pb-6 last:border-b-0">
                      <div className="flex justify-between mb-2">
                        <div className="flex-1">
                          <p className="font-medium">{item.description}</p>
                          {item.details && (
                            <p className="text-sm text-gray-500 mt-1">{item.details}</p>
                          )}
                          <p className="text-sm text-gray-600 mt-1">
                            Formato: {item.format} | Quantità: {item.quantity}
                          </p>
                        </div>
                        <div className="text-right">
                          {item.discount_percent > 0 ? (
                            <>
                              <p className="text-sm text-gray-400 line-through">{formatCurrency(item.unit_price)} cad.</p>
                              <p className="font-medium text-green-600">{formatCurrency(item.unit_price * (1 - item.discount_percent / 100))} cad.</p>
                              <p className="text-xs text-green-600">-{item.discount_percent}% sconto</p>
                            </>
                          ) : (
                            <p className="font-medium">{formatCurrency(item.unit_price)} cad.</p>
                          )}
                          <p className="font-bold mt-1">{formatCurrency(item.total)}</p>
                          <button
                            type="button"
                            onClick={() => handleCloneItem(index)}
                            data-testid={`clone-quote-item-${index}`}
                            className="mt-2 inline-flex items-center px-2 py-1 text-xs text-purple-700 hover:text-white hover:bg-purple-600 border border-purple-300 rounded transition-colors"
                            title="Clona prodotto"
                          >
                            <Copy className="w-3 h-3 mr-1" />
                            Clona
                          </button>
                        </div>
                      </div>
                      
                      {/* Tabella Simulazione Sconti Quantità - SEMPLIFICATA */}
                      {simulations.length > 0 && (
                        <div className="mt-4 bg-blue-50 rounded-lg p-4 border border-blue-200">
                          {/* Titolo */}
                          <div className="text-base font-bold text-gray-800 mb-3">
                            Sconti quantità
                          </div>
                          
                          {/* Tabella semplificata */}
                          <div className="text-sm">
                            <div className="space-y-1">
                              {simulations.map((sim, idx) => {
                                const hasFreeShipping = sim.minQty >= 120;
                                return (
                                  <div 
                                    key={idx} 
                                    className={`flex justify-between py-2 px-3 rounded ${
                                      sim.isCurrentTier 
                                        ? 'bg-yellow-100 border-l-4 border-yellow-500 font-semibold' 
                                        : 'bg-white'
                                    }`}
                                  >
                                    <span className="text-gray-700">
                                      {sim.minQty}{sim.maxQty < 999 ? `-${sim.maxQty}` : '+'} pz
                                      {sim.isCurrentTier && <span className="ml-2 text-xs text-yellow-700">← tu sei qui</span>}
                                      {hasFreeShipping && <span className="ml-2 text-xs text-blue-600 font-semibold">🚚 spedizione gratuita</span>}
                                    </span>
                                    <span className="font-medium">
                                      {sim.discount > 0 ? (
                                        <span className="text-green-600">-{sim.discount}%</span>
                                      ) : (
                                        <span className="text-gray-400">nessuno sconto</span>
                                      )}
                                      <span className="text-gray-700 ml-3">{formatCurrency(sim.pricePerUnit)} cad.</span>
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Servizi aggiuntivi */}
              {quote.services && quote.services.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h3 className="font-medium mb-3">Servizi Aggiuntivi</h3>
                  <div className="space-y-2">
                    {quote.services.map((service, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span>{service.description}</span>
                        <span className="font-medium">{formatCurrency(service.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Totali */}
              <div className="mt-6 pt-4 border-t space-y-2">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotale (IVA esclusa)</span>
                  <span>{formatCurrency(quote.subtotal)}</span>
                </div>
                {Number(quote.shipping_cost || 0) > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Costo spedizione (IVA esclusa)</span>
                    <span>{formatCurrency(quote.shipping_cost)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-600">
                  <span>IVA 22%</span>
                  <span>{formatCurrency(quote.vat_amount)}</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-gray-900 pt-2 border-t">
                  <span>Totale (IVA inclusa)</span>
                  <span>{formatCurrency(quote.total)}</span>
                </div>

                {/* MOL — Margine Operativo Lordo (admin only, no IVA) */}
                {(() => {
                  const items = quote.items || [];
                  const revenue = items.reduce((sum, it) => sum + (it.total || 0), 0);
                  const totalCost = items.reduce((sum, it) => sum + ((it.unit_cost || 0) * (it.quantity || 0)), 0);
                  const fiscal = computeFiscalSummary(items, 'alcohol_content');
                  const totalBottles = fiscal.totalBottles;
                  const hasAnyCost = items.some(it => (it.unit_cost || 0) > 0);
                  const hasFiscal = fiscal.accisa > 0 || fiscal.contrassegno > 0;
                  if (!hasAnyCost && !hasFiscal) return null;
                  const molNet = revenue - totalCost - fiscal.accisa - fiscal.contrassegno;
                  const molNetPct = revenue > 0 ? (molNet / revenue) * 100 : 0;
                  const color = molNet >= 0 ? 'text-green-700 bg-green-50 border-green-200' : 'text-red-700 bg-red-50 border-red-200';
                  const hasGifts = items.some(it => (Number(it.quantity) || 0) > 0 && (Number(it.total) || 0) === 0);
                  return (
                    <div className={`mt-3 p-3 border rounded-lg space-y-2 ${color}`} data-testid="quote-detail-mol">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-medium">MOL netto — Margine Operativo Lordo</p>
                          <p className="text-xs opacity-75">Ricavi {formatCurrency(revenue)} − Costi {formatCurrency(totalCost + fiscal.accisa + fiscal.contrassegno)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold">{formatCurrency(molNet)}</p>
                          <p className="text-xs font-semibold">{revenue > 0 ? `${molNetPct.toFixed(1)}%` : '—'}</p>
                        </div>
                      </div>

                      {/* Breakdown per riga (utile con omaggi) */}
                      <div className="pt-2 border-t border-current border-opacity-20">
                        <p className="text-xs font-semibold mb-2 flex items-center gap-2">
                          🔎 Dettaglio per prodotto
                          {hasGifts && <span className="text-[10px] font-normal opacity-75">— omaggi evidenziati in viola, i loro costi sono sottratti dal MOL</span>}
                        </p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-[11px]" data-testid="quote-detail-mol-per-line">
                            <thead>
                              <tr className="opacity-75 border-b border-current border-opacity-30">
                                <th className="text-left font-semibold py-1 pr-2">Prodotto</th>
                                <th className="text-right font-semibold py-1 px-1">Qty</th>
                                <th className="text-right font-semibold py-1 px-1">Ricavo</th>
                                <th className="text-right font-semibold py-1 px-1">Ind.</th>
                                <th className="text-right font-semibold py-1 px-1">Accisa</th>
                                <th className="text-right font-semibold py-1 px-1">Contr.</th>
                                <th className="text-right font-semibold py-1 pl-1">Netto</th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((it, idx) => {
                                const qty = Number(it.quantity) || 0;
                                const rev = Number(it.total) || 0;
                                const indCost = (Number(it.unit_cost) || 0) * qty;
                                const accisa = computeAccisaPerBottle(it.format, it.alcohol_content) * qty;
                                const contr = computeContrassegnoPerBottle(it.format) * qty;
                                const net = rev - indCost - accisa - contr;
                                const isGift = qty > 0 && rev === 0;
                                return (
                                  <tr key={idx} className={`border-b border-current border-opacity-10 ${isGift ? 'bg-purple-100/40' : ''}`} data-testid={`quote-detail-mol-line-${idx}`}>
                                    <td className="py-1 pr-2">
                                      <div className="flex items-center gap-1.5">
                                        <span className="truncate max-w-[220px]">{it.description || `Riga ${idx + 1}`}</span>
                                        {isGift && (
                                          <span className="inline-block px-1.5 py-0.5 rounded bg-purple-600 text-white text-[9px] font-bold uppercase tracking-wide">
                                            Omaggio
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-[10px] opacity-70">{it.format} · {(it.alcohol_content || 0)}° alc</div>
                                    </td>
                                    <td className="text-right py-1 px-1 tabular-nums">{qty}</td>
                                    <td className="text-right py-1 px-1 tabular-nums">{formatCurrency(rev)}</td>
                                    <td className="text-right py-1 px-1 tabular-nums">{formatCurrencyMillesimi(indCost)}</td>
                                    <td className="text-right py-1 px-1 tabular-nums">{formatCurrencyMillesimi(accisa)}</td>
                                    <td className="text-right py-1 px-1 tabular-nums">{formatCurrencyMillesimi(contr)}</td>
                                    <td className={`text-right py-1 pl-1 tabular-nums font-bold ${net >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(net)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="pt-2 border-t border-current border-opacity-20 space-y-1 text-sm">
                        {hasAnyCost && (
                          <div className="flex justify-between items-center" data-testid="quote-detail-industrial-costs">
                            <span className="font-medium">Costi industriali ({totalBottles} {totalBottles === 1 ? 'bottiglia' : 'bottiglie'})</span>
                            <span className="font-semibold">{formatCurrencyMillesimi(totalCost)}</span>
                          </div>
                        )}
                        {fiscal.accisa > 0 && (
                          <div className="flex justify-between items-center" data-testid="quote-detail-accisa">
                            <span className="font-medium">Accisa (€10,3552/L alcol puro)</span>
                            <span className="font-semibold">{formatCurrencyMillesimi(fiscal.accisa)}</span>
                          </div>
                        )}
                        {fiscal.contrassegno > 0 && (
                          <div className="flex justify-between items-center" data-testid="quote-detail-contrassegno">
                            <span className="font-medium">Contrassegno di stato</span>
                            <span className="font-semibold">{formatCurrencyMillesimi(fiscal.contrassegno)}</span>
                          </div>
                        )}
                        {(hasAnyCost || hasFiscal) && (
                          <div className="flex justify-between items-center pt-2 mt-1 border-t border-current border-opacity-30" data-testid="quote-detail-total-costs">
                            <span className="font-semibold">Totale costi (industriali + accisa + contrassegno)</span>
                            <span className="font-bold">{formatCurrencyMillesimi(totalCost + fiscal.accisa + fiscal.contrassegno)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Piano di Pagamento */}
              {quote.payment_schedule_type === 'split_50_50' && quote.scheduled_payments && quote.scheduled_payments.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    💰 Piano di Pagamento
                  </h3>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                    {quote.scheduled_payments.map((payment, index) => (
                      <div key={payment.id || index} className="flex justify-between items-center py-2 border-b border-amber-200 last:border-b-0">
                        <div>
                          <p className="font-medium text-amber-900">{payment.label}</p>
                          <p className="text-sm text-amber-700">{payment.due_description}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-amber-900">{formatCurrency(payment.amount)}</p>
                          <p className="text-sm text-amber-600">{payment.percentage}%</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Note e Condizioni */}
            {quote.terms_and_conditions && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-3">Termini e Condizioni</h2>
                <p className="text-gray-700 whitespace-pre-wrap">{quote.terms_and_conditions}</p>
              </div>
            )}

            {quote.admin_notes && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="font-medium text-yellow-900 mb-2">Note Admin (private)</h3>
                <p className="text-yellow-800 text-sm">{quote.admin_notes}</p>
              </div>
            )}

            {/* Note Cliente */}
            {quote.customer_notes && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-2 flex items-center">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Note Cliente
                </h3>
                <p className="text-blue-800 text-sm whitespace-pre-wrap">{quote.customer_notes}</p>
              </div>
            )}
          </div>

          {/* Colonna destra */}
          <div className="space-y-6">
            {/* Info Cliente */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Building2 className="w-5 h-5 mr-2" />
                Cliente
              </h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Azienda</p>
                  <p className="font-medium">{quote.customer_data?.business_name || 'N/A'}</p>
                </div>
                {quote.customer_data?.vat_number && (
                  <div>
                    <p className="text-sm text-gray-500">P.IVA</p>
                    <p className="font-medium">{quote.customer_data.vat_number}</p>
                  </div>
                )}
                <div className="pt-3 border-t">
                  <div className="flex items-start mb-2">
                    <Mail className="w-4 h-4 mr-2 mt-1 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium">{quote.customer_data?.email}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Info Preventivo */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                Informazioni
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Data emissione</span>
                  <span className="font-medium">{formatDate(quote.issue_date)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Validità</span>
                  <span className="font-medium">{quote.validity_days} giorni</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Scadenza</span>
                  <span className="font-medium">{formatDate(quote.expiry_date)}</span>
                </div>
                {quote.created_by && (
                  <div className="flex justify-between text-sm pt-3 border-t">
                    <span className="text-gray-500">Creato da</span>
                    <span className="font-medium">Admin</span>
                  </div>
                )}
              </div>
            </div>

            {/* Allegati */}
            {quote.attachments && quote.attachments.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4">Allegati</h2>
                <div className="space-y-2">
                  {quote.attachments.map((attachment, index) => (
                    <a
                      key={index}
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center p-2 hover:bg-gray-50 rounded transition-colors"
                    >
                      <FileText className="w-4 h-4 mr-2 text-gray-400" />
                      <span className="text-sm text-purple-600 hover:text-purple-800">
                        {attachment.filename || `Allegato ${index + 1}`}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Timeline */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                Timeline
              </h2>
              <div className="space-y-3">
                {/* Quote Created */}
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3"></div>
                  <div>
                    <p className="text-sm font-medium">Preventivo creato</p>
                    <p className="text-xs text-gray-500">
                      {new Date(quote.createdAt).toLocaleString('it-IT', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
                
                {/* Activity Log Events */}
                {quote.activityLog && quote.activityLog.length > 0 && (
                  <>
                    {quote.activityLog.map((activity, index) => {
                      const getActivityColor = (type) => {
                        switch(type) {
                          case 'email_sent': return 'bg-blue-600';
                          case 'status_changed': return 'bg-green-600';
                          case 'converted_to_order': return 'bg-purple-600';
                          default: return 'bg-gray-600';
                        }
                      };
                      
                      return (
                        <div key={index} className="flex items-start">
                          <div className={`w-2 h-2 ${getActivityColor(activity.type)} rounded-full mt-2 mr-3`}></div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              {activity.description}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(activity.timestamp).toLocaleString('it-IT', {
                                day: '2-digit',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                            {activity.details && activity.details.sentBy && (
                              <p className="text-xs text-gray-400 mt-1">
                                da {activity.details.sentBy}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
                
                {/* Last Update (fallback if no activity log) */}
                {(!quote.activityLog || quote.activityLog.length === 0) && quote.updatedAt && quote.updatedAt !== quote.createdAt && (
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3"></div>
                    <div>
                      <p className="text-sm font-medium">Ultimo aggiornamento</p>
                      <p className="text-xs text-gray-500">
                        {new Date(quote.updatedAt).toLocaleString('it-IT', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default QuoteDetail;
